import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { RefreshCw, Brain, AlertCircle, Clock, TrendingUp, CheckCircle2, FileText } from "lucide-react";

const API_BASE = "";

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    green: 'bg-green-50 text-green-800 border-green-200',
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    yellow: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    orange: 'bg-orange-50 text-orange-800 border-orange-200'
  };

  return (
    <Card className={`${colorClasses[color]} border-2`}>
      <CardContent className="p-2 sm:p-3 md:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">{title}</p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1">{value}</p>
            {subtitle && <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {Icon && <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 opacity-50" />}
        </div>
      </CardContent>
    </Card>
  );
}

function HybridDashboardPage() {
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState({});
  const [error, setError] = useState(null);
  const [reasoningDialog, setReasoningDialog] = useState({ open: false, title: '', content: '', description: '' });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const statsResponse = await fetch('/api/hybrid/stats');
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      const merchantsResponse = await fetch('/api/hybrid/merchants');
      if (merchantsResponse.ok) {
        const merchantsData = await merchantsResponse.json();
        setMerchants(merchantsData.merchants || []);
      }

      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Hybrid orchestrator not reachable. Is it running on port 8095?');
      setLoading(false);
    }
  };

  const handleReanalyze = async (merchantUrl) => {
    setReanalyzing(prev => ({ ...prev, [merchantUrl]: true }));
    
    try {
      const encodedUrl = encodeURIComponent(merchantUrl);
      const response = await fetch(`/api/hybrid/reanalyze/${encodedUrl}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Re-analysis complete!\nMCC: ${result.mcc_primary} (${(result.confidence * 100).toFixed(1)}% confidence)`);
        fetchData();
      } else {
        alert('❌ Re-analysis failed');
      }
    } catch (err) {
      console.error('Error re-analyzing:', err);
      alert('❌ Network error');
    } finally {
      setReanalyzing(prev => ({ ...prev, [merchantUrl]: false }));
    }
  };

  const showReasoning = (title, content, description) => {
    setReasoningDialog({
      open: true,
      title,
      content: content || 'No reasoning available',
      description
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return 'N/A';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading LLM Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="w-6 h-6" />
              <span>Connection Error</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchData} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const avgProcessingTime = merchants.length > 0 
    ? merchants.reduce((sum, m) => sum + (m.processing_time_sec || 0), 0) / merchants.length 
    : 0;

  return (
    <div className="max-w-[1500px] mx-auto p-2 sm:p-3 md:p-4 space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base sm:text-lg md:text-xl font-bold">🧠 LLM Classification Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time accuracy tracking and performance metrics
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          title="Total Merchants"
          value={stats?.total_merchants || 0}
          icon={Brain}
          color="blue"
          subtitle="In database"
        />
        <StatCard
          title="LLM Processed"
          value={stats?.mistral_processed || 0}
          icon={CheckCircle2}
          color="green"
          subtitle="AI classified"
        />
        <StatCard
          title="Average Confidence"
          value={stats?.mistral_avg_confidence ? `${(stats.mistral_avg_confidence * 100).toFixed(1)}%` : 'N/A'}
          icon={TrendingUp}
          color="purple"
          subtitle="LLM accuracy"
        />
        <StatCard
          title="Avg Processing Time"
          value={formatDuration(avgProcessingTime)}
          icon={Clock}
          color="orange"
          subtitle="Total pipeline"
        />
      </div>

      {/* Stage Timing Breakdown */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <span>MCC Classification Pipeline Breakdown</span>
          </CardTitle>
          <CardDescription>Average time spent in each stage of the 3-stage LLM pipeline</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {(() => {
              const validMerchants = merchants.filter(m => m.stage1_time_ms > 0 || m.stage2_time_ms > 0 || m.stage3_time_ms > 0);
              if (validMerchants.length === 0) {
                return (
                  <div className="col-span-4 text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No timing data available yet</p>
                  </div>
                );
              }

              const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
              const stage1Avg = avg(validMerchants.map(m => (m.stage1_time_ms || 0) / 1000));
              const stage2Avg = avg(validMerchants.map(m => (m.stage2_time_ms || 0) / 1000));
              const stage3Avg = avg(validMerchants.map(m => (m.stage3_time_ms || 0) / 1000));
              const totalAvg = stage1Avg + stage2Avg + stage3Avg;

              return (
                <>
                  <div className="p-6 bg-blue-100 rounded-lg border-2 border-blue-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                      <p className="text-sm font-semibold text-blue-900">Stage 1: AI Extract</p>
                    </div>
                    <p className="text-3xl font-bold text-blue-700">{formatDuration(stage1Avg)}</p>
                    <p className="text-xs text-blue-600 mt-1">Business understanding</p>
                  </div>

                  <div className="p-6 bg-green-100 rounded-lg border-2 border-green-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                      <p className="text-sm font-semibold text-green-900">Stage 2: LLM MCC</p>
                    </div>
                    <p className="text-3xl font-bold text-green-700">{formatDuration(stage2Avg)}</p>
                    <p className="text-xs text-green-600 mt-1">Keyword matching</p>
                  </div>

                  <div className="p-6 bg-purple-100 rounded-lg border-2 border-purple-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                      <p className="text-sm font-semibold text-purple-900">Stage 3: Final LLM</p>
                    </div>
                    <p className="text-3xl font-bold text-purple-700">{formatDuration(stage3Avg)}</p>
                    <p className="text-xs text-purple-600 mt-1">Classification decision</p>
                  </div>

                  <div className="p-6 bg-orange-100 rounded-lg border-2 border-orange-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
                      <p className="text-sm font-semibold text-orange-900">Total MCC Time</p>
                    </div>
                    <p className="text-3xl font-bold text-orange-700">{formatDuration(totalAvg)}</p>
                    <p className="text-xs text-orange-600 mt-1">Complete pipeline</p>
                  </div>
                </>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>LLM Processed Merchants</CardTitle>
          <CardDescription>
            All merchants classified by LLM with confidence scores and reasoning
          </CardDescription>
        </CardHeader>
        <CardContent>
          {merchants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No merchants processed yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Primary MCC</TableHead>
                    <TableHead>Secondary MCC</TableHead>
                    <TableHead>Tertiary MCC</TableHead>
                    <TableHead>Stage Timing</TableHead>
                    <TableHead>Total Time</TableHead>
                    <TableHead>Last Analysis</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.map((merchant, idx) => {
                    const isLowConfidence = merchant.mcc_primary_conf < 0.85;
                    return (
                      <TableRow key={idx} className={isLowConfidence ? 'bg-red-50' : ''}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{merchant.merchant_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-xs">
                              {merchant.merchant_url}
                            </p>
                          </div>
                        </TableCell>
                        
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-semibold">{merchant.mcc_primary || 'N/A'}</p>
                              <span className="text-xs text-gray-500">
                                ({(merchant.mcc_primary_conf * 100).toFixed(1)}%)
                              </span>
                            </div>
                            {merchant.mcc_primary_desc && (
                              <p className="text-xs text-muted-foreground">{merchant.mcc_primary_desc}</p>
                            )}
                            {merchant.primary_reasoning && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs"
                                onClick={() => showReasoning(
                                  `Primary MCC: ${merchant.mcc_primary}`,
                                  merchant.primary_reasoning,
                                  merchant.mcc_primary_desc
                                )}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Reasoning
                              </Button>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {merchant.mcc_secondary ? (
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <p className="font-semibold">{merchant.mcc_secondary}</p>
                                <span className="text-xs text-gray-500">
                                  ({(merchant.mcc_secondary_conf * 100).toFixed(1)}%)
                                </span>
                              </div>
                              {merchant.mcc_secondary_desc && (
                                <p className="text-xs text-muted-foreground">{merchant.mcc_secondary_desc}</p>
                              )}
                              {merchant.secondary_reasoning && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-xs"
                                  onClick={() => showReasoning(
                                    `Secondary MCC: ${merchant.mcc_secondary}`,
                                    merchant.secondary_reasoning,
                                    merchant.mcc_secondary_desc
                                  )}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Reasoning
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {merchant.mcc_tertiary ? (
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <p className="font-semibold">{merchant.mcc_tertiary}</p>
                                <span className="text-xs text-gray-500">
                                  ({(merchant.mcc_tertiary_conf * 100).toFixed(1)}%)
                                </span>
                              </div>
                              {merchant.mcc_tertiary_desc && (
                                <p className="text-xs text-muted-foreground">{merchant.mcc_tertiary_desc}</p>
                              )}
                              {merchant.tertiary_reasoning && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-xs"
                                  onClick={() => showReasoning(
                                    `Tertiary MCC: ${merchant.mcc_tertiary}`,
                                    merchant.tertiary_reasoning,
                                    merchant.mcc_tertiary_desc
                                  )}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Reasoning
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="space-y-1">
                            {merchant.stage1_time_ms > 0 || merchant.stage2_time_ms > 0 || merchant.stage3_time_ms > 0 ? (
                              <>
                                <div className="flex items-center space-x-1 text-xs">
                                  <span className="text-blue-600 font-semibold">S1:</span>
                                  <span className="text-blue-800">{formatDuration((merchant.stage1_time_ms || 0) / 1000)}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-xs">
                                  <span className="text-green-600 font-semibold">S2:</span>
                                  <span className="text-green-800">{formatDuration((merchant.stage2_time_ms || 0) / 1000)}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-xs">
                                  <span className="text-purple-600 font-semibold">S3:</span>
                                  <span className="text-purple-800">{formatDuration((merchant.stage3_time_ms || 0) / 1000)}</span>
                                </div>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">No timing data</span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDuration(merchant.processing_time_sec)}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(merchant.last_analysis)}
                        </TableCell>

                        <TableCell>
                          {isLowConfidence ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReanalyze(merchant.merchant_url)}
                              disabled={reanalyzing[merchant.merchant_url]}
                            >
                              {reanalyzing[merchant.merchant_url] ? (
                                <>
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Re-analyze
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReanalyze(merchant.merchant_url)}
                              disabled={reanalyzing[merchant.merchant_url]}
                            >
                              {reanalyzing[merchant.merchant_url] ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span>Accuracy Metrics</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">High Confidence (≥90%)</p>
              <p className="text-2xl font-bold text-green-600">
                {merchants.filter(m => m.mcc_primary_conf >= 0.9).length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Medium Confidence (75-89%)</p>
              <p className="text-2xl font-bold text-yellow-600">
                {merchants.filter(m => m.mcc_primary_conf >= 0.75 && m.mcc_primary_conf < 0.9).length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Low Confidence (&lt;75%)</p>
              <p className="text-2xl font-bold text-red-600">
                {merchants.filter(m => m.mcc_primary_conf < 0.75).length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <span>Processing Speed (LLM)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Fastest Processing</p>
              <p className="text-2xl font-bold text-green-600">
                {merchants.length > 0 
                  ? formatDuration(Math.min(...merchants.map(m => m.processing_time_sec || Infinity)))
                  : 'N/A'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Average Processing</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatDuration(avgProcessingTime)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Slowest Processing</p>
              <p className="text-2xl font-bold text-red-600">
                {merchants.length > 0 
                  ? formatDuration(Math.max(...merchants.map(m => m.processing_time_sec || 0)))
                  : 'N/A'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm">
            <div>
              <p className="text-muted-foreground">Hybrid Orchestrator</p>
              <p className="font-semibold">✅ Connected (Port 8095)</p>
            </div>
            <div>
              <p className="text-muted-foreground">Ollama Service</p>
              <p className="font-semibold">✅ Running (Port 11434)</p>
            </div>
            <div>
              <p className="text-muted-foreground">AI Model</p>
              <p className="font-semibold">Gemma3:12B LLM</p>
            </div>
            <div>
              <p className="text-muted-foreground">Total Processed</p>
              <p className="font-semibold">{merchants.length} merchants</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={reasoningDialog.open} onOpenChange={(open) => setReasoningDialog({ ...reasoningDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{reasoningDialog.title}</DialogTitle>
            {reasoningDialog.description && (
              <DialogDescription>{reasoningDialog.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
            <p className="text-sm whitespace-pre-wrap">{reasoningDialog.content}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default HybridDashboardPage;
